import { AccountApi, RegisterAccountParams, RegisterWorkerParams, SendActivationCodeParams, UpdateAccountParams } from "./interface";

import { ApiHooksProps } from "../interface";
import { removeSpacesFromString } from "../utils";
import { request } from "../request";
import { useCallback } from "react";

const hook = ({ setError, setLoading, setSuccess, serverPath, token }: ApiHooksProps): AccountApi => {


  const changePassword = useCallback(async (customerId: string, oldPassword: string, newPassword: string, confirmPassword: string) => {
    const path = `${serverPath}/accounts/change-password/`;
    const body = {
      ConfirmPassword: confirmPassword,
      NewPassword: newPassword,
      OldPassword: oldPassword,
      UserId: customerId
    };
    
    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('Password updated!')
    } catch (error : any) {
      if (typeof error === 'string') {
        setError(error);
      }
      else {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  const getAccountDetails = useCallback(async (id: string) => {
    const path = `${serverPath}/accounts/user/${id}`;
    request(path, { method: 'GET', token });
  }, [serverPath, token]);

  const register = useCallback(async (data: RegisterAccountParams) => {
    const path = `${serverPath}/accounts/register/`;
    const body = {
      AccountType: data.type,
      BranchName: data.branchName,
      CompanyName: data.companyName,
      ConfirmPassword: data.passwordConfirm,
      CountryId: data.countryId,
      Email: data.email,
      FirstName: data.firstName,
      LastName: data.lastName,
      Password: data.password,
      PhoneNumber: data.phone,
      PromoCode: data.promoCode,
      UserName: data.email
    };

    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('User registered!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  const registerWorker = useCallback(async (data: RegisterWorkerParams) => {
    const path = `${serverPath}/accounts/register-worker/`;
    const body = {
      AddressString: data.addressString,
      CommutingDistance: data.commutingDistance,
      CommutingDistanceType: data.commutingDistanceType,
      ConfirmPassword: data.passwordConfirm,
      Email: data.email,
      FirstName: data.firstName,
      LastName: data.lastName,
      Password: data.password,
      PhoneNumber: data.phoneNumber,
      Postcode: data.postcode,
      Town: data.town,
      UserName: data.email,
      WebsiteLink: data.websiteLink
    };

    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('Worker registered!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  const sendActivationCode = useCallback(async (body: SendActivationCodeParams) => {
    const path = `${serverPath}/accounts/activate/`;

    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('Activation code sent!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  const updateAccountDetails = useCallback(async (id: string, data: UpdateAccountParams) => {
    const path = `${serverPath}/accounts/user/`;
    const body = {
      AccountPhoneNumber: removeSpacesFromString(data.accountPhoneNumber),
      DaysToPayInvoices: data.daysToPayInvoices || 0,
      DefaultPhotos: data.defaultPhotos || 0,
      Email: data.accountsEmailAddress || data.email,
      FirstNAme: data.firstName,
      Id: id,
      LastName: data.lastName,
      UserName: data.name || data.userName,
      UserUpsellPrice: data.userUpsellPrice || 0,
    };
    
    setLoading(true);
    try {
      await request(path, { body, token });
      setSuccess('User account updated!')
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);

    return;
  }, [serverPath, token]);

  return {
    changePassword,
    getAccountDetails,
    register,
    registerWorker,
    sendActivationCode,
    updateAccountDetails,
  }
}

export default hook;